import React, {forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState} from "react";
import Dialog from "../../base/dialog";
import {DialogActions, DialogContent, DialogTitle, Divider, LinearProgress, MenuItem, Tooltip, Typography} from "@mui/material";
import Button from "../../base/button/button";
import ImportCsvFileDropzone from "../../app-specific/dropzone/import-csv-file";
import {exportCSVFile} from "../../../../core/services/utils/utils";
import useIsMounted from "../../../hooks/use-is-mounted";
import Api from "../../../../core/services/api/api";
import useCachedPriceLevelsDictionaryController from "../../../hooks/use-price-level-dictionary";
import LoadingLottieAnimation from "../../app-specific/lottie-animations/loading";
import Regexes from "../../../../core/models/regexes";
import ImportingLottieAnimation from "../../app-specific/lottie-animations/importing";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import Form, {MuiRFF, Yup} from '../../form';
import Grid2 from "@mui/material/Unstable_Grid2";
import PhoneTextField from "../../text-fields/phone-textfield";
import EnvService from "../../../../core/services/env";
import SummaryInfoCard from "../../app-specific/summary-cards/summary-info-card";
import usePartnerConfiguration from "../../../hooks/use-partner-configuration";

const importCustomersSteps = {
    upload: 'upload',
    import: 'import',
    summary: 'summary',
}

const importCustomerStatuses = {
    success: 'success',
    error: 'error',
    pending: 'pending',
    warning: 'warning',
    na: 'na',
}

const ImportCustomersDialog = ({open, close, onSuccess}) => {
    const formRef = useRef();
    const [step, setStep] = useState(importCustomersSteps.upload);
    const [formData, setFormData] = useState({paymentTerms: [], priceLevels: [], countries: [], provinces: []});
    const [loadingFormData, setLoadingFormData] = useState(false);
    const [state, setState] = useState({data: [], chosenIndex: -1})
    const [importing, setImporting] = useState(false);
    const [reviewData, setReviewData] = useState(null);
    const {getValue} = usePartnerConfiguration();
    const [autoGenerateCode, setAutoGenerateCode] = useState(getValue((ids) => ids.autoGenerateCustomerNumber) ?? true);
    const {getPriceLevelDictionary} = useCachedPriceLevelsDictionaryController()
    const isMounted = useIsMounted();

    /**
     * With each change in the [open] state of the dialog, sets the [autoGenerateCode] state value from the partner configuration.
     */
    useEffect(() => {
        setAutoGenerateCode(getValue((ids) => ids.autoGenerateCustomerNumber) ?? true);
    }, [open, getValue])

    const correctionFormId = useMemo(() => 'correction-form', [])

    /**
     * Gets all the form data necessary for the import process.
     * @type {function(): Promise<void>}
     */
    const fetchFormData = useCallback(async () => {
        setLoadingFormData(true);
        const [getUserProfileFormResponse, priceLevelDict] = await Promise.all([
            Api.getUserProfileForm(),
            getPriceLevelDictionary(),
        ])
        if (!isMounted())
            return;
        setLoadingFormData(false);
        if (getUserProfileFormResponse?.resultFlag) {
            setFormData({
                paymentTerms: getUserProfileFormResponse?.data?.paymentTerms,
                priceLevels: getUserProfileFormResponse?.data?.priceLevels?.map(p => ({
                    ...p,
                    title: priceLevelDict?.find(d => d.priceLevelId === p.id)?.title ?? p.title,
                })) ?? [],
                countries: getUserProfileFormResponse?.data?.countries ?? [],
                provinces: getUserProfileFormResponse?.data?.provinces ?? [],
            })
        }
    }, [getPriceLevelDictionary, isMounted])

    /**
     * As soon as the component mounts, we fetch the form data.
     */
    useEffect(() => {
        if (open) {
            void fetchFormData();
        }
    }, [fetchFormData, open]);

    /**
     * Resets the state of the dialog when it is opened.
     */
    useLayoutEffect(() => {
        if (open) {
            setStep(importCustomersSteps.upload);
            setState({data: [], chosenIndex: -1});
            setReviewData(null);
            setImporting(false);
        }
    }, [open])

    /**
     * Closes the dialog and calls the onSuccess callback with the customers that were created.
     * @type {(function(): void)}
     */
    const closeDialog = useCallback(() => {
        const customers = state.data.map(d => d.customer.createdEntry).map(e => !!e);
        if (customers.length) {
            onSuccess(customers);
        }
        close();
    }, [close, onSuccess, state.data])

    const uploadRequiredFields = useMemo(() => [
        {name: "name", title: "Name", generator: () => ""},
        {name: "phone", title: "Phone", generator: () => "",},
        {name: "country", title: "Country", generator: () => "",},
        {name: "province", title: "Province/State", generator: () => "",},
        {name: "city", title: "City", generator: () => "",},
        {name: "address", title: "Address", generator: () => "",},
        {name: "aptNo", title: "Apt/Suite", generator: () => "",},
        {name: "postalCode", title: "Postal/Zip Code", generator: () => "",},
        {name: "priceLevel", title: "Price Level", generator: () => "",},
        {name: "paymentTerms", title: "Payment Terms Code", generator: () => "",},
        {name: "creditLimit", title: "Credit Limit", generator: () => "",},
        {name: "memberFirstName", title: "Member First Name", generator: () => "",},
        {name: "memberLastName", title: "Member Last Name", generator: () => "",},
        {name: "memberEmail", title: "Member Email", generator: () => "",},
        {name: "memberPhone", title: "Member Phone", generator: () => "",},
        ...(autoGenerateCode ? [] : [{name: "customerNumber", title: "Customer Code", generator: () => `='`}])
    ], [autoGenerateCode])

    /**
     * Creates and exports a sample csv formatted file that has the correct format for this dropzone.
     * @type {function(): void}
     */
    const downloadSampleFile = useCallback(() => {
        const entries = Array(10)
            .fill(null)
            .map((_, e) => Object.fromEntries(
                uploadRequiredFields.map(({title, generator}) => [title, generator(e + 1)])
            ))
        exportCSVFile(entries, "import-customers.csv");
    }, [uploadRequiredFields])

    /**
     * Parses the Name of the entry.
     * @type {function(*): *}
     */
    const parseName = useCallback((name) => {
        return name.trim();
    }, [])

    /**
     * Parses the Customer Number of the entry.
     * @type {function(*): string}
     */
    const parseCustomerNumber = useCallback((customerNumber) => {
        return customerNumber?.toString()?.trim()?.replaceAll('=', '')?.replaceAll('"', '')
    }, []);

    /**
     * Parses the Phone of the entry.
     * @type {(function(*): (*))}
     */
    const parsePhone = useCallback((phone) => {
        const clean = phone.replace(/[^0-9]/g, '');
        if (clean.length === 10) {
            return clean;
        }
        if (clean.length === 11 && clean.startsWith('1')) {
            return clean.substring(1);
        }
        return clean;
    }, [])

    /**
     * Parses the Address of the entry.
     * @type {function(*): *}
     */
    const parseAddress = useCallback((address) => {
        return address.trim();
    }, [])

    /**
     * Parses the ApartmentNo of the entry.
     * @type {function(*): *}
     */
    const parseApartmentNo = useCallback((aptNo) => {
        return aptNo.trim();
    }, [])

    /**
     * Parses the City of the entry.
     * @type {function(*): *}
     */
    const parseCity = useCallback((city) => {
        return city?.trim();
    }, [])

    /**
     * Parses the Country of the entry.
     * @type {(function(*, *): (null|*))}
     */
    const parseCountry = useCallback((country, formData) => {
        if (!country) {
            return null;
        }
        return formData.countries.find(c => c.name === country);
    }, [])

    /**
     * Parses the Province of the entry.
     * @type {(function(*, *, *): (null|*))}
     */
    const parseProvince = useCallback((province, country, formData) => {
        if (!province) {
            return null;
        }
        return formData.provinces.find(p => {
            if (p.name !== province) {
                return false;
            }
            const parsedCountry = parseCountry(country, formData);
            if (!parsedCountry) {
                return false;
            }
            return p.countryId === parsedCountry.id;
        });
    }, [parseCountry])

    /**
     * Parses the PostalCode of the entry.
     * @type {function(*): *}
     */
    const parsePostalCode = useCallback((postalCode) => {
        return postalCode.trim();
    }, [])

    /**
     * Parses the FirstName of the entry.
     * @type {function(*): *}
     */
    const parseFirstName = useCallback((firstName) => {
        return firstName.trim();
    }, [])

    /**
     * Parses the LastName of the entry.
     * @type {function(*): *}
     */
    const parseLastName = useCallback((lastName) => {
        return lastName.trim();
    }, [])

    /**
     * Parses the Email of the entry.
     * @type {function(*): *}
     */
    const parseEmail = useCallback((email) => {
        return email.trim();
    }, [])

    /**
     * Parses the UserPhone of the entry.
     * @type {(function(*): (*))}
     */
    const parseUserPhone = useCallback((userPhone) => {
        const clean = userPhone.replace(/[^0-9]/g, '');
        if (clean.length === 10) {
            return clean;
        }
        if (clean.length === 11) {
            return clean.substring(1);
        }
        return userPhone;
    }, [])

    /**
     * Parses the PriceLevel of the entry.
     * @type {function(*, *): *}
     */
    const parsePriceLevel = useCallback((priceLevel, formData) => {
        return formData.priceLevels.find(p => p.title === priceLevel);
    }, [])

    /**
     * Parses the PaymentTerms of the entry.
     * @type {function(*, *): *}
     */
    const parsePaymentTerms = useCallback((paymentTerms, formData) => {
        return formData.paymentTerms.find(p => p.code === paymentTerms?.toUpperCase());
    }, [])

    /**
     * Parses the CreditLimit of the entry.
     * @type {function(*): number}
     */
    const parseCreditLimit = useCallback((creditLimit) => {
        return parseFloat(creditLimit);
    }, [])

    /**
     * Imports a customer by code.
     * @type {function(*): *}
     */
    const createQueueData = useCallback((_entry) => {
        const entry = {
            customerNumber: _entry?.customerNumber ?? undefined,
            name: _entry?.name ?? "",
            phone: _entry?.phone ?? "",
            country: _entry?.country ?? "",
            province: _entry?.province ?? "",
            city: _entry?.city ?? "",
            address: _entry?.address ?? "",
            aptNo: _entry?.aptNo ?? "",
            postalCode: _entry?.postalCode ?? "",
            priceLevel: _entry?.priceLevel ?? "",
            paymentTerms: _entry?.paymentTerms ?? "",
            creditLimit: _entry?.creditLimit ?? "",
            memberFirstName: _entry?.memberFirstName ?? "",
            memberLastName: _entry?.memberLastName ?? "",
            memberEmail: _entry?.memberEmail ?? "",
            memberPhone: _entry?.memberPhone ?? "",
        }
        // if all values are empty strings, do nothing
        if (!Object.values(entry).some(v => !!v)) {
            return;
        }
        const parsedMemberEntry = {
            firstName: parseFirstName(entry.memberFirstName),
            lastName: parseLastName(entry.memberLastName),
            email: parseEmail(entry.memberEmail),
            phone: parseUserPhone(entry.memberPhone),
        };
        const canCreateMember = !!parsedMemberEntry.firstName && !!parsedMemberEntry.lastName && !!parsedMemberEntry.email;
        return {
            entry: {...entry},
            status: importCustomerStatuses.pending,
            customer: {
                createdEntry: null,
                parsedEntry: {
                    ...(!autoGenerateCode ? {customerNumber: parseCustomerNumber(entry.customerNumber)} : {}),
                    name: parseName(entry.name),
                    phone: parsePhone(entry.phone),
                    country: parseCountry(entry.country, formData),
                    province: parseProvince(entry.province, entry.country, formData),
                    city: parseCity(entry.city),
                    address: parseAddress(entry.address),
                    aptNo: parseApartmentNo(entry.aptNo),
                    postalCode: parsePostalCode(entry.postalCode),
                    priceLevel: parsePriceLevel(entry.priceLevel, formData),
                    paymentTerms: parsePaymentTerms(entry.paymentTerms, formData),
                    creditLimit: parseCreditLimit(entry.creditLimit),
                },
                status: importCustomerStatuses.na,
                message: '',
            },
            member: {
                createdEntry: null,
                canCreate: canCreateMember,
                parsedEntry: parsedMemberEntry,
                status: importCustomerStatuses.na,
                message: '',
            },
        }
    }, [autoGenerateCode, formData, parseAddress, parseApartmentNo, parseCity, parseCountry, parseCreditLimit, parseCustomerNumber, parseEmail, parseFirstName, parseLastName, parseName, parsePaymentTerms, parsePhone, parsePostalCode, parsePriceLevel, parseProvince, parseUserPhone])

    /**
     * Adds the parsed array to the queue.
     * @type {(function(*): void)}
     */
    const onFileUploaded = useCallback((parsedArray) => {
        if (!parsedArray.length)
            return;
        setState({data: parsedArray.map(e => createQueueData(e)).filter(e => !!e), chosenIndex: -1})
        setStep(importCustomersSteps.import);
    }, [createQueueData])

    const maxWidth = useMemo(() => {
        switch (step) {
            case importCustomersSteps.upload:
                return 'sm';
            case importCustomersSteps.import:
                if (!!reviewData)
                    return 'sm';
                return 'md';
            case importCustomersSteps.summary:
                return 'sm';
            default:
                return 'sm';
        }
    }, [step, reviewData])

    /**
     * Validates the data of the customer and member.
     * @type {(function({customer: *, member: *, entry: *}): (boolean))}
     */
    const validateData = useCallback(({customer, member}) => {
        if (!customer) {
            return false;
        }
        const customerErroneous = {};
        if (customer.parsedEntry.name.length < 1) {
            customerErroneous.name = customer.parsedEntry.name;
        }
        if (!autoGenerateCode && customer.parsedEntry.customerNumber.length < 1) {
            customerErroneous.customerNumber = customer.parsedEntry.customerNumber;
        }
        if (!!customer.parsedEntry.phone.length && customer.parsedEntry.phone.length !== 10) {
            customerErroneous.phone = customer.parsedEntry.phone;
        }
        if (!customer.parsedEntry.country) {
            customerErroneous.country = customer.parsedEntry.country;
            customerErroneous.province = customer.parsedEntry.province;
        }
        if (!customer.parsedEntry.province) {
            customerErroneous.country = customer.parsedEntry.country;
            customerErroneous.province = customer.parsedEntry.province;
        }
        // City is not required
        // if (customer.parsedEntry.city.length < 1) {
        //     customerErroneous.city = customer.parsedEntry.city;
        // }
        if (customer.parsedEntry.address.length < 1) {
            customerErroneous.address = customer.parsedEntry.address;
        }
        // APT NO is not required
        // if (customer.parsedEntry.aptNo.length < 1) {
        //     customerErroneous.aptNo = customer.parsedEntry.aptNo;
        // }
        // Postal Code is not required
        // if (customer.parsedEntry.postalCode.length < 1) {
        //     customerErroneous.postalCode = customer.parsedEntry.postalCode;
        // }
        if (!customer.parsedEntry.priceLevel) {
            customerErroneous.priceLevel = customer.parsedEntry.priceLevel;
        }
        if (!customer.parsedEntry.paymentTerms) {
            customerErroneous.paymentTerms = customer.parsedEntry.paymentTerms;
        }
        if (customer.parsedEntry.creditLimit < 0 || isNaN(customer.parsedEntry.creditLimit)) {
            customerErroneous.creditLimit = isNaN(customer.parsedEntry.creditLimit) ? null : customer.parsedEntry.creditLimit;
        }

        const memberErroneous = {};
        if (member.canCreate) {
            if (member.parsedEntry.firstName.length < 1) {
                memberErroneous.memberfirstName = member.parsedEntry.firstName;
            }
            if (member.parsedEntry.lastName.length < 1) {
                memberErroneous.lastName = member.parsedEntry.lastName;
            }
            if (member.parsedEntry.email.length < 1 || !member.parsedEntry.email.match(Regexes.Email)) {
                memberErroneous.email = member.parsedEntry.email;
            }
            // phone is not required
            if (!!member.parsedEntry.phone.length && member.parsedEntry.phone.length !== 10) {
                memberErroneous.phone = member.parsedEntry.phone;
            }
        }
        if (Object.keys(customerErroneous).length || Object.keys(memberErroneous).length) {
            setReviewData({
                ...(!!Object.keys(customerErroneous).length ? {customer: customerErroneous} : {}),
                ...(!!Object.keys(memberErroneous).length ? {member: memberErroneous} : {}),
            });
            return false;
        } else {
            setReviewData(null);
            return true;
        }
    }, [autoGenerateCode])

    /**
     * Imports the customer and member.
     * @type {(function({customer: {parsedEntry: *}, member: {parsedEntry: *}}, *): Promise<void>)}
     */
    const importCustomerAndMember = useCallback(async ({customer: {parsedEntry: customer}, member: {parsedEntry: member, canCreate: canCreateMember}}, chosenIndex) => {
        setImporting(true);
        await new Promise(r => setTimeout(r, 1000));
        const customerResponse = await Api.createCustomer({
            customerNumber: customer.customerNumber,
            name: customer.name,
            paymentTermsCode: customer.paymentTerms.code,
            creditLimit: customer.creditLimit,
            priceLevelId: customer.priceLevel.id,
            phoneNumber: customer.phone ?? '',
            address: {
                address: customer.address ?? '',
                aptNo: customer.aptNo ?? '',
                city: customer.city ?? '',
                provinceId: customer.province.id,
                postalCode: customer.postalCode ?? '',
            }
        }, true)
        if (!isMounted())
            return;
        if (!customerResponse?.resultFlag) {
            // customer fail
            setState(prevState => ({
                ...prevState,
                data: prevState.data.map((d, i) => {
                    if (i === chosenIndex) {
                        return {
                            ...d,
                            customer: {
                                ...d.customer,
                                createdEntry: null,
                                status: importCustomerStatuses.error,
                                message: customerResponse?.message ?? "An error occurred while importing the customer."
                            }
                        }
                    }
                    return d;
                })
            }))
        } else {
            // customer success
            setState(prevState => ({
                ...prevState,
                data: prevState.data.map((d, i) => {
                    if (i === chosenIndex) {
                        return {
                            ...d,
                            customer: {
                                ...d.customer,
                                createdEntry: customerResponse?.data,
                                status: importCustomerStatuses.success,
                                message: customerResponse?.message ?? "Customer imported successfully."
                            }
                        }
                    }
                    return d;
                })
            }))
        }

        if (!canCreateMember || !customerResponse?.resultFlag) {
            // we are done since we can't create a member or the customer failed
            setImporting(false);
            setState(prevState => ({
                ...prevState,
                chosenIndex: -1,
                data: prevState.data.map((d, i) => {
                    if (i === chosenIndex) {
                        return {
                            ...d,
                            status: d.customer.status === importCustomerStatuses.success
                                ? importCustomerStatuses.success
                                : importCustomerStatuses.error,
                        }
                    }
                    return d;
                })
            }));
        } else if (canCreateMember) {
            // we can create a member
            await new Promise(r => setTimeout(r, 1000))
            const memberResponse = await Api.fastCreateUserFromSpireCustomer({
                code: customerResponse?.data?.code,
                dontNotifyUser: true,
                newUserDS: {
                    firstName: member.firstName,
                    lastName: member.lastName,
                    email: member.email,
                    phone: member.phone,
                },
            }, true);
            if (!isMounted())
                return;
            if (memberResponse?.resultFlag) {
                // member success
                setState(prevState => ({
                    ...prevState,
                    data: prevState.data.map((d, i) => {
                        if (i === chosenIndex) {
                            return {
                                ...d,
                                member: {
                                    ...d.member,
                                    createdEntry: memberResponse?.data,
                                    status: importCustomerStatuses.success,
                                    message: memberResponse?.message ?? "Member imported successfully."
                                }
                            }
                        }
                        return d;
                    })
                }))
            } else {
                // member fail
                setState(prevState => ({
                    ...prevState,
                    data: prevState.data.map((d, i) => {
                        if (i === chosenIndex) {
                            return {
                                ...d,
                                member: {
                                    ...d.member,
                                    createdEntry: null,
                                    status: importCustomerStatuses.error,
                                    message: memberResponse?.message ?? "An error occurred while importing the member."
                                }
                            }
                        }
                        return d;
                    })
                }))
            }
            setImporting(false);
            // we need to determine if overall it was a success, error, or a warning
            setState(prevState => ({
                ...prevState,
                chosenIndex: -1,
                data: prevState.data.map((d, i) => {
                    if (i === chosenIndex) {
                        return {
                            ...d,
                            status: d.customer.status === importCustomerStatuses.success && d.member.status === importCustomerStatuses.success
                                ? importCustomerStatuses.success
                                : d.customer.status === importCustomerStatuses.error && d.member.status === importCustomerStatuses.error
                                    ? importCustomerStatuses.error
                                    : importCustomerStatuses.warning,
                        }
                    }
                    return d;
                })
            }))
        }
    }, [isMounted])

    /**
     * If the step is import, then we check if there is a chosenIndex entry, if not, we choose the first pending entry.
     * If there is no pending entry, then we move to the summary step.
     */
    useEffect(() => {
        if (!open)
            return;
        if (step !== importCustomersSteps.import)
            return;
        if (state.chosenIndex !== -1)
            return;
        const firstPendingIndex = state.data.findIndex(d => d.status === importCustomerStatuses.pending);
        if (firstPendingIndex === -1) {
            setStep(importCustomersSteps.summary);
            return;
        }
        setState(prevState => ({...prevState, chosenIndex: firstPendingIndex}));
    }, [state.data, state.chosenIndex, step, open])

    /**
     * Validates the data of the customer and member and then if it is valid, it imports the customer and member.
     */
    useEffect(() => {
        if (!open)
            return;
        if (step !== importCustomersSteps.import)
            return;
        if (state.chosenIndex === -1)
            return;
        if (importing)
            return;
        if (!!reviewData)
            return;
        const data = state.data[state.chosenIndex];
        if (!data.reviewed) {
            if (!validateData(data))
                return;
        }
        void importCustomerAndMember(data, state.chosenIndex);
    }, [state.chosenIndex, step, importing, validateData, reviewData, state.data, importCustomerAndMember, open])

    /**
     * Updates the data of the customer and member.
     * @type {(function({member: *, customer: *}): void)}
     */
    const onCorrectData = useCallback(({member, customer}) => {
        setReviewData(null);
        setState(prevState => ({
            ...prevState,
            data: prevState.data.map((d, i) => {
                if (i === state.chosenIndex) {
                    return {
                        ...d,
                        reviewed: true,
                        customer: {
                            ...d.customer,
                            parsedEntry: {
                                ...d.customer.parsedEntry,
                                ...customer,
                            }
                        },
                        member: {
                            ...d.member,
                            parsedEntry: {
                                ...d.member.parsedEntry,
                                ...member,
                            }
                        }
                    }
                }
                return d;
            })
        }))
    }, [state.chosenIndex])

    const progressPercentage = useMemo(() => {
        return Math.min(
            Math.round(((state.chosenIndex + 1) / (state.data.length === 0 ? 1 : state.data.length)) * 100),
            100
        );
    }, [state.chosenIndex, state.data])

    const summaryCard = useMemo(() => ({
        header: {
            title: 'Import Summary',
            value: "",
            loadingValue: "Loading...",
        },
        body: [
            {
                id: 'totalCustomers',
                title: 'Total Customers',
                value: state.data.length,
            },
            {
                id: 'successCustomers',
                title: 'Successful Customers',
                value: state.data.filter(d => d.customer.status === importCustomerStatuses.success).length,
            },
            {
                id: 'failedCustomers',
                title: 'Failed Customers',
                value: state.data.filter(d => d.customer.status === importCustomerStatuses.error).length,
            },
            {
                id: 'totalMembers',
                title: 'Total Members',
                value: state.data.filter(d => d.member.canCreate).length,
            },
            {
                id: 'successMembers',
                title: 'Successful Members',
                value: state.data.filter(d => d.member.status === importCustomerStatuses.success).length,
            },
            {
                id: 'failedMembers',
                title: 'Failed Members',
                value: state.data.filter(d => d.member.status === importCustomerStatuses.error).length,
            },
        ],
    }), [state.data])

    const hasAnyErrors = useMemo(() => {
        return state.data.some(d => d.customer.status === importCustomerStatuses.error);
    }, [state.data])
    const hasAnyComplete = useMemo(() => {
        return state.data.some(d => d.customer.status === importCustomerStatuses.success);
    }, [state.data])

    /**
     * Downloads the imported customers.
     * @type {(function(): void)}
     */
    const downloadImportedCustomers = useCallback(() => {
        const importedCustomers = state.data.filter(d => d.customer.status === importCustomerStatuses.success).map(d => d.customer.createdEntry).map(e => {
            const res = {...e};
            delete res['id'];
            return res;
        });
        exportCSVFile(importedCustomers, "imported-customers");
    }, [state.data])

    /**
     * Downloads the failed customers.
     * @type {(function(): void)}
     */
    const downloadFailedCustomers = useCallback(() => {
        const failedCustomers = state.data.filter(d => d.customer.status === importCustomerStatuses.error).map(d => d.customer.parsedEntry);
        exportCSVFile(failedCustomers, "failed-customers");
    }, [state.data])

    /**
     * Downloads the report of the import process.
     * @type {(function(): void)}
     */
    const downloadReport = useCallback(() => {
        const report = state.data.map(d => ({
            ...(!autoGenerateCode ? {"Customer Number": d.customer.parsedEntry.customerNumber} : {}),
            "Name": d.customer.parsedEntry.name,
            "Phone": d.customer.parsedEntry.phone,
            "Country": d.customer.parsedEntry.country?.name,
            "Province/State": d.customer.parsedEntry.province?.name,
            "City": d.customer.parsedEntry.city,
            "Address": d.customer.parsedEntry.address,
            "Apt/Suite": d.customer.parsedEntry.aptNo,
            "Postal/Zip Code": d.customer.parsedEntry.postalCode,
            "Price Level": d.customer.parsedEntry.priceLevel?.title,
            "Payment Terms Code": d.customer.parsedEntry.paymentTerms?.code,
            "Credit Limit": d.customer.parsedEntry.creditLimit,
            "Member First Name": d.member.parsedEntry.firstName,
            "Member Last Name": d.member.parsedEntry.lastName,
            "Member Email": d.member.parsedEntry.email,
            "Member Phone": d.member.parsedEntry.phone,
            "Customer Status": d.customer.status,
            "Customer Message": d.customer.message,
            "Member Status": d.member.status,
            "Member Message": d.member.message,
        }))
        exportCSVFile(report, "import-report.csv");
    }, [autoGenerateCode, state.data])

    return (
        <>
            <Dialog
                open={open}
                setOpen={() => closeDialog()}
                maxWidth={maxWidth}
                loading={step === importCustomersSteps.import}
                className={'bound-dialog'}
                scroll={'paper'}
                aria-labelledby="scroll-dialog-title"
            >
                <DialogTitle id={'scroll-dialog-title'}>
                    <p className={`text-lg text-color-1 my-3 font-weight-bold text-center`}>
                        {
                            step === importCustomersSteps.upload
                                ? "Upload Customers"
                                : step === importCustomersSteps.import
                                    ? `Importing ${state.chosenIndex + 1}/${state.data.length} Customers`
                                    : "Summary"
                        }
                    </p>
                    <Divider className='divider'/>
                </DialogTitle>
                <DialogContent className={'px-4 pt-4 d-flex flex-column'}>
                    {
                        step === importCustomersSteps.upload &&
                        <>
                            {
                                loadingFormData &&
                                <LoadingLottieAnimation width={'80%'}/>
                            }
                            {
                                !loadingFormData &&
                                <>
                                    <Typography variant={'body2'} className={'mb-2'}>
                                        Please upload a CSV file to start the import process. You can download a sample file by clicking the button below for the correct format.
                                    </Typography>
                                    <Typography
                                        component={'div'}
                                        variant={'body2'}
                                        sx={{
                                            width: '100%',
                                            marginBlockEnd: 3,
                                            strong: {color: 'secondary.main'},
                                            b: {color: 'tertiary.main'},
                                            lineHeight: 2
                                        }}
                                    >
                                        Notes: <br/>
                                        <ul>
                                            <li>Ensure that for phone number fields, only numbers are entered, we will try to parse them as much as we can.</li>
                                            <li>Ensure that the country and province fields are entered correctly from the available values in the system</li>
                                            <li>Ensure that the price level and payment terms are entered form the available values in the system</li>
                                            <li>If Member information are provided, then we will try to create a member for the customer as well.</li>
                                        </ul>
                                    </Typography>
                                    <div className={'p-3'}>
                                        <ImportCsvFileDropzone
                                            onParsed={onFileUploaded}
                                            requiredFields={uploadRequiredFields}
                                        />
                                    </div>
                                </>
                            }
                        </>
                    }
                    {
                        step === importCustomersSteps.import &&
                        <>
                            {
                                !reviewData &&
                                <div className={'d-flex flex-column px-4 pb-5'}>
                                    <ImportingLottieAnimation width={'80%'} height={'50vh'}/>
                                    <Tooltip
                                        open={true}
                                        placement={'top'}
                                        arrow
                                        title={`Now submitting ${state.chosenIndex + 1}${
                                            (state.chosenIndex + 1) === 1 ? 'st' : (state.chosenIndex + 1) === 2 ? 'nd' : (state.chosenIndex + 1) === 3 ? 'rd' : 'th'
                                        } Customer and Member. Please wait...`}
                                    >
                                        <div className={'d-flex flex-column w-100 py-3 align-items-center'}>
                                            <LinearProgress
                                                className={'w-80'}
                                                variant={'buffer'}
                                                color={'secondary'}
                                                value={progressPercentage}
                                                valueBuffer={progressPercentage}
                                            />
                                            <p className={'identifier'}>{`${progressPercentage}%`}</p>
                                        </div>
                                    </Tooltip>
                                    <Typography variant={'body2'} className={'mb-2'} textAlign={'center'}>
                                        We are submitting your customer and member information to the system. Please do not close this dialog until the process is complete.
                                    </Typography>
                                    <Button
                                        variant={'text'}
                                        color={'primary'}
                                        onClick={closeDialog}
                                        size={'medium'}
                                        sx={{width: 'fit-content', marginInline: 'auto'}}
                                    >
                                        Stop Importing and exit
                                    </Button>
                                </div>
                            }
                            {
                                !!reviewData &&
                                <div className={'d-flex flex-column px-4 pb-4'}>
                                    <Typography variant={'body2'} className={'mb-3'}>
                                        Please review the following errors and correct them before proceeding. If you believe the data is correct, you can proceed by clicking the button below.
                                    </Typography>
                                    <CorrectionForm
                                        autoGenerateCode={autoGenerateCode}
                                        ref={formRef}
                                        info={state.data[state.chosenIndex]}
                                        data={reviewData}
                                        id={correctionFormId}
                                        onSubmit={onCorrectData}
                                        formData={formData}
                                    />
                                </div>
                            }
                        </>
                    }
                    {
                        step === importCustomersSteps.summary &&
                        <>
                            <Typography variant={'body2'} className={'mb-2'}>
                                The import process has been completed. Below is a summary of the import process.
                            </Typography>
                            <SummaryInfoCard
                                header={summaryCard.header}
                                body={summaryCard.body}
                            />
                            <div className={'d-flex flex-column w-100'} style={{gap: 12}}>
                                {
                                    hasAnyComplete &&
                                    <Button
                                        variant={'outlined'}
                                        color={'primary'}
                                        onClick={downloadImportedCustomers}
                                        size={'medium'}
                                        fullWidth
                                    >
                                        Download Imported Customers
                                    </Button>
                                }
                                {
                                    hasAnyErrors &&
                                    <Button
                                        variant={'outlined'}
                                        color={'primary'}
                                        onClick={downloadFailedCustomers}
                                        size={'medium'}
                                        fullWidth
                                    >
                                        Download Failed Customers
                                    </Button>
                                }
                                <Button
                                    variant={'contained'}
                                    color={'primary'}
                                    onClick={downloadReport}
                                    size={'medium'}
                                    fullWidth
                                >
                                    Download Report
                                </Button>
                                <Button
                                    variant={'text'}
                                    color={'primary'}
                                    onClick={closeDialog}
                                    size={'medium'}
                                    fullWidth
                                >
                                    Close
                                </Button>
                            </div>
                        </>
                    }
                </DialogContent>
                {
                    step === importCustomersSteps.upload &&
                    <DialogActions className={'p-0 w-100 m-0 d-flex flex-column'}>
                        <Divider className='divider'/>
                        <div className={'d-flex align-items-center justify-content-between m-0 w-100'}>
                            <Button
                                variant={'dialog'}
                                color={'primary'}
                                onClick={closeDialog}
                                borderRadius={'0 8px 0 8px'}
                            >
                                Close
                            </Button>
                            <Button
                                variant={'dialog'}
                                color={'primary'}
                                onClick={downloadSampleFile}
                                borderRadius={'8px 0 8px 0'}
                            >
                                Download Sample File
                            </Button>
                        </div>
                    </DialogActions>
                }
                {
                    step === importCustomersSteps.import && !!reviewData &&
                    <DialogActions className={'p-0 w-100 m-0 d-flex flex-column'}>
                        <Divider className='divider'/>
                        <div className={'d-flex align-items-center justify-content-between m-0 w-100'}>
                            <Button
                                variant={'dialog'}
                                color={'primary'}
                                onClick={closeDialog}
                                borderRadius={'0 8px 0 8px'}
                            >
                                Stop Importing
                            </Button>
                            <Button
                                variant={'dialog'}
                                color={'primary'}
                                borderRadius={'8px 0 8px 0'}
                                onClick={() => formRef.current?.submit()}
                            >
                                Proceed
                            </Button>
                        </div>
                    </DialogActions>
                }
            </Dialog>
        </>
    );
}


const CorrectionForm = forwardRef(({id, info, data, onSubmit, formData, autoGenerateCode}, ref) => {
    const schema = React.useMemo(() => Yup.object().shape({
        customerNumber: autoGenerateCode ? Yup.string().trim().nullable() : Yup.string().trim().nullable().required(ValidateMessages.required),
        name: Yup.string().trim().nullable(),
        phone: Yup.string().trim().nullable().phoneNo(ValidateMessages.incorrectType("phone"), 10),
        priceLevel: Yup.mixed().nullable(),
        paymentTerms: Yup.mixed().nullable(),
        creditLimit: Yup.string().nullable(),
        country: Yup.mixed().nullable(),
        province: Yup.mixed().nullable(),
        city: Yup.string().trim().nullable(),
        postalCode: Yup.string().trim().nullable(),
        address: Yup.string().trim().nullable(),
        aptNo: Yup.string().trim().nullable(),

        memberFirstName: Yup.string().trim().nullable(),
        memberLastName: Yup.string().trim().nullable(),
        memberEmail: Yup.string().trim().nullable().email(ValidateMessages.incorrectType("email")),
        memberPhone: Yup.string().trim().nullable().phoneNo(ValidateMessages.incorrectType("phone"), 10),
    }), [autoGenerateCode])

    const validate = MuiRFF.makeValidate(schema);
    const required = MuiRFF.makeRequired(schema);

    const formRef = useRef();

    useImperativeHandle(ref, () => ({
        submit: () => {
            formRef.current?.submit();
        }
    }), [])

    const initialValues = useMemo(() => ({
        customerNumber: data.customer?.customerNumber ?? '',
        name: data.customer?.name ?? '',
        phone: data.customer?.phone ?? '',
        priceLevel: data.customer?.priceLevel ?? null,
        paymentTerms: data.customer?.paymentTerms ?? null,
        creditLimit: data.customer?.creditLimit ?? '',
        country: data.customer?.country ?? null,
        province: data.customer?.province ?? null,
        city: data.customer?.city ?? '',
        postalCode: data.customer?.postalCode ?? '',
        address: data.customer?.address ?? '',
        aptNo: data.customer?.aptNo ?? '',
        memberFirstName: data.member?.firstName ?? '',
        memberLastName: data.member?.lastName ?? '',
        memberEmail: data.member?.email ?? '',
        memberPhone: data.member?.phone ?? '',
    }), [data])

    const customerKeys = useMemo(() => Object.keys(data.customer ?? {}), [data.customer])

    const memberKeys = useMemo(() => Object.keys(data.member ?? {}), [data.member])

    /**
     * Submits the form.
     * @type {(function(*): ({paymentTerms: string}|undefined))}
     */
    const submitForm = useCallback((values) => {
        if (!values.paymentTerms && customerKeys.includes('paymentTerms')) {
            return {
                paymentTerms: "Payment Terms is required."
            }
        }
        if (!values.priceLevel && customerKeys.includes('priceLevel')) {
            return {
                priceLevel: "Price Level is required."
            }
        }
        if (!values.country && customerKeys.includes('country')) {
            return {
                country: "Country is required."
            }
        }
        if (!values.province && customerKeys.includes('province')) {
            return {
                province: "Province is required."
            }
        }
        if (!values.name && customerKeys.includes('name')) {
            return {
                name: "Name is required."
            }
        }
        if (customerKeys.includes('creditLimit')) {
            if (Number.isNaN(Number(values.creditLimit))) {
                return {
                    creditLimit: "Credit Limit must be a number."
                }
            }
            if (Number(values.creditLimit) < 0) {
                return {
                    creditLimit: "Credit Limit must be greater than or equal to 0."
                }
            }
        }
        onSubmit({
            member: memberKeys.reduce((acc, key) => {
                acc[key] = values[`member${key.charAt(0).toUpperCase()}${key.slice(1)}`];
                return acc;
            }, {}),
            customer: customerKeys.reduce((acc, key) => {
                acc[key] = values[key];
                return acc;
            }, {}),
        })
    }, [customerKeys, memberKeys, onSubmit])

    return (
        <>

            <Form
                onSubmit={submitForm}
                validate={validate}
                initialValues={initialValues}
                formProps={{
                    id: id,
                }}
            >
                {({form, values}) => {
                    formRef.current = form;
                    const provinces = formData.provinces.filter(province => province.countryId === values.country?.id);
                    const USBased = values.country?.name === EnvService.USATitle;
                    return (
                        <Grid2 container spacing={2}>
                            <Grid2 xs={12}>
                                <div className={'d-flex flex-column'}>
                                    {
                                        Object.entries(info?.customer?.parsedEntry ?? {}).filter(([key]) => !customerKeys.includes(key)).map(([key, value]) => (
                                            <div key={key} className={'mb-2'}>
                                                <Typography variant={'body2'}>
                                                    <Typography component={'span'} variant={'body2'} fontWeight={600} color={'tertiary.main'}>
                                                        {
                                                            key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')
                                                        }
                                                    </Typography>: {
                                                    typeof value === 'string' || typeof value === 'number'
                                                        ? value
                                                        : (value?.title ?? value?.name ?? value?.description ?? value?.code)
                                                }
                                                </Typography>
                                            </div>
                                        ))
                                    }
                                </div>
                            </Grid2>
                            {
                                customerKeys.map((key) => {
                                    let input;
                                    switch (key) {
                                        case 'customerNumber': {
                                            input = (
                                                <MuiRFF.TextField
                                                    name={'customerNumber'}
                                                    label={'Customer Number'}
                                                    required={required.customerNumber}
                                                    disabled={autoGenerateCode}
                                                />
                                            )
                                            break;
                                        }
                                        case 'name': {
                                            input = (
                                                <MuiRFF.TextField
                                                    name={'name'}
                                                    label={'Name'}
                                                    required={required.name}
                                                />
                                            )
                                            break;
                                        }
                                        case 'phone': {
                                            input = (
                                                <PhoneTextField
                                                    form
                                                    label={'Phone'}
                                                    name={'phone'}
                                                    required={required.phone}
                                                />
                                            );
                                            break;
                                        }
                                        case "priceLevel": {
                                            input = (
                                                <MuiRFF.TextField
                                                    select
                                                    name={'priceLevel'}
                                                    label={'Price Level'}
                                                    required={required.priceLevel}
                                                    SelectProps={{
                                                        renderValue: (value) => value?.displayTitle ?? value?.title ?? "",
                                                    }}
                                                >
                                                    {formData.priceLevels.map((priceLevel) => (
                                                        <MenuItem key={priceLevel.id} value={priceLevel}>
                                                            {priceLevel.displayTitle ?? priceLevel?.title ?? ""}
                                                        </MenuItem>
                                                    ))}
                                                </MuiRFF.TextField>
                                            )
                                            break;
                                        }
                                        case "paymentTerms": {
                                            input = (
                                                <MuiRFF.TextField
                                                    select
                                                    name={'paymentTerms'}
                                                    label={'Payment Term'}
                                                    required={required.paymentTerms}
                                                    SelectProps={{
                                                        renderValue: (value) => value?.description ?? "",
                                                    }}
                                                >
                                                    {formData.paymentTerms.map((paymentTerm) => (
                                                        <MenuItem key={paymentTerm.id} value={paymentTerm}>
                                                            {paymentTerm.description} ({paymentTerm.code})
                                                        </MenuItem>
                                                    ))}
                                                </MuiRFF.TextField>
                                            )
                                            break;
                                        }
                                        case "creditLimit": {
                                            input = (
                                                <MuiRFF.TextField
                                                    name={'creditLimit'}
                                                    label={'Credit Limit'}
                                                    required={required.creditLimit}
                                                    type={'number'}
                                                />
                                            )
                                            break;
                                        }
                                        case "country": {
                                            input = (
                                                <MuiRFF.TextField
                                                    select
                                                    name={'country'}
                                                    label={'Country'}
                                                    required={required.country}
                                                    disabled={formData.countries.length === 0}
                                                    SelectProps={{
                                                        renderValue: (value) => value?.name ?? "",
                                                        onChange: (e) => {
                                                            form.change(e.target.name, e.target.value);
                                                            form.change('province', null);
                                                        }
                                                    }}
                                                >
                                                    {formData.countries.map((country) => (
                                                        <MenuItem key={country.id} value={country}>
                                                            {country.name}
                                                        </MenuItem>
                                                    ))}
                                                </MuiRFF.TextField>
                                            )
                                            break;
                                        }
                                        case "province": {
                                            input = (
                                                <MuiRFF.TextField
                                                    select
                                                    name={'province'}
                                                    label={!values.country ? "Province/State" : USBased ? 'State' : 'Province'}
                                                    required={required.province}
                                                    disabled={provinces.length === 0}
                                                    SelectProps={{
                                                        renderValue: (value) => value?.name ?? "",
                                                    }}
                                                >
                                                    {provinces.map((province) => (
                                                        <MenuItem key={province.id} value={province}>
                                                            {province.name}
                                                        </MenuItem>
                                                    ))}
                                                </MuiRFF.TextField>
                                            )
                                            break;
                                        }
                                        case "city": {
                                            input = (
                                                <MuiRFF.TextField
                                                    name={'address'}
                                                    label={'Address'}
                                                    required={required.address}
                                                />
                                            )
                                            break;
                                        }
                                        case "postalCode": {
                                            input = (
                                                <MuiRFF.TextField
                                                    name={'postalCode'}
                                                    label={!values.country ? "Postal/Zip Code" : USBased ? 'Zip Code' : 'Postal Code'}
                                                    required={required.postalCode}
                                                />
                                            )
                                            break;
                                        }
                                        case "address": {
                                            input = (
                                                <MuiRFF.TextField
                                                    name={'address'}
                                                    label={'Address'}
                                                    required={required.address}
                                                />
                                            )
                                            break;
                                        }
                                        case "aptNo": {
                                            input = (
                                                <MuiRFF.TextField
                                                    name={'aptNo'}
                                                    label={'Apartment No.'}
                                                    required={required.aptNo}
                                                />
                                            )
                                            break;
                                        }
                                        default:
                                            break;
                                    }
                                    return (
                                        <Grid2 xs={12} key={`customer-${key}`} className={'d-flex flex-column'}>
                                            {input}
                                            {
                                                !!(info.entry[key] ?? "") &&
                                                <Typography variant={'caption'} marginBlockStart={1}>
                                                    Raw value: {(info.entry[key] ?? "")}
                                                </Typography>
                                            }
                                        </Grid2>
                                    );
                                })
                            }
                            {
                                info?.member?.canCreate &&
                                <Grid2 xs={12}>
                                    <div className={'d-flex flex-column mt-4'}>
                                        {
                                            Object.entries(info?.member?.parsedEntry ?? {}).filter(([key]) => !memberKeys.includes(key)).map(([key, value]) => (
                                                <div key={key} className={'mb-2'}>
                                                    <Typography variant={'body2'}>
                                                        <Typography component={'span'} variant={'body2'} fontWeight={600} color={'tertiary.main'}>
                                                            Member {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
                                                        </Typography>: {
                                                        typeof value === 'string' || typeof value === 'number'
                                                            ? value
                                                            : (value?.title ?? value?.name ?? value?.description ?? value?.code) ?? (info.entry[`member${key.charAt(0).toUpperCase()}${key.slice(1)}`] ?? "")
                                                    }
                                                    </Typography>
                                                </div>
                                            ))
                                        }
                                    </div>
                                </Grid2>
                            }
                            {
                                memberKeys.map((key) => {
                                    let input;
                                    switch (key) {
                                        case 'firstName': {
                                            input = (
                                                <MuiRFF.TextField
                                                    name={'memberFirstName'}
                                                    label={'First Name'}
                                                    required={required.memberFirstName}
                                                />
                                            )
                                            break;
                                        }
                                        case 'lastName': {
                                            input = (
                                                <MuiRFF.TextField
                                                    name={'memberLastName'}
                                                    label={'Last Name'}
                                                    required={required.memberLastName}
                                                />
                                            )
                                            break;
                                        }
                                        case 'phone': {
                                            input = (
                                                <PhoneTextField
                                                    form
                                                    label={'Member Phone'}
                                                    name={'memberPhone'}
                                                    required={required.memberPhone}
                                                />
                                            );
                                            break;
                                        }
                                        case 'email': {
                                            input = (
                                                <MuiRFF.TextField
                                                    name={'memberEmail'}
                                                    label={'Member Email'}
                                                    required={required.memberEmail}
                                                />
                                            );
                                            break;
                                        }
                                        default:
                                            break;
                                    }
                                    return (
                                        <Grid2 xs={12} key={`customer-${key}`} className={'d-flex flex-column'}>
                                            {input}
                                            {
                                                !!(info.entry[`member${key.charAt(0).toUpperCase()}${key.slice(1)}`] ?? "") &&
                                                <Typography variant={'caption'} marginBlockStart={1}>
                                                    Raw value: {(info.entry[`member${key.charAt(0).toUpperCase()}${key.slice(1)}`] ?? "")}
                                                </Typography>
                                            }
                                        </Grid2>
                                    );
                                })
                            }
                        </Grid2>
                    )
                }}
            </Form>
        </>
    );
})

export default ImportCustomersDialog;